import React, { useMemo, useState, useEffect, useRef, Suspense, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";
import ReactToPrint from "react-to-print";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Accordion, Tabs, Tab, Alert } from "react-bootstrap";
import { groupAndAverageTasks } from "../services/GroupAndAverageTasks";
import {
  getClass,
  getClassAssessments,
  getTeachers,
  getClassesAssessmentResults,
  getIndividualStudentResults,
  getCountryId,
  getEntityTags,
  getTagDefinitions,
  getScreenerAssessmentIds,
  checkStandardisedScoresEnabled
} from "../services/APIClient";
import "./SencoStudentsResultsOverview.css";
import { Fallback, BigSpinner } from "../services/SpinnerComponent";
import { groupClassResults } from "../services/GroupAndAverageTasks";

import SENWatchlistButton from "./SENWatchlistButton";
import ReportActionCard from "./ReportActionCard";
import ActionCard from "./ActionCard";
import DeleteAnswerModal from "./DeleteAnswerModal";
import ShareActionCard from "./ShareActionCard";
import PrintActionCardSenco from "./PrintActionCardSenco";

const StandardScoreRegionLockModal = React.lazy(() =>
  import("./StandardScoreRegionLockModal")
);

const StandardScoreUnavailableModal = React.lazy(() =>
  import("./StandardScoreUnavailableModal")
);


// Lazy-loaded components
const SencoAssessmentDropdown = React.lazy(() => import("./SencoAssessmentDropdown"));
const SencoStudentsResultsTable = React.lazy(() => import("./SencoStudentsResultsTable"));
const RecommendationsButton = React.lazy(() => import("./RecommendationsButton"));
const ReturnToSencoResults = React.lazy(() => import("./ReturnToSencoResults"));
const SencoTaskScoreGraph = React.lazy(() => import("./SencoTaskScoreGraph"));
const PieThresholdGraph = React.lazy(() => import("./PieThresholdGraph"));
const ClassroomAnalysis = React.lazy(() => import("./ClassroomAnalysis"));
const TaskScoresComparisonChart = React.lazy(() => import("./TaskScoresComparisonChart"));
const ClassroomProgressTabContent = React.lazy(() => import("./ClassroomProgressTabContent"));
const ScreenerPieCharts = React.lazy(() => import("./ScreenerPieCharts"));
const TaskScoresComparisonChartSchool = React.lazy(() => import("./TaskScoresComparisonChartSchool"));
const StudentPerformancePieChart = React.lazy(() => import("./StudentPerformancePieChart"));
const AllStudentsPerformancePieChart = React.lazy(() => import("./AllStudentsPerformancePieChart"));
const NotScreenedTab = React.lazy(() => import("./NotScreenedTab"));
const AllStudentsIndividualSearch = React.lazy(() => import("./AllStudentsIndividualSearch"));
const StandardScoresGraph = React.lazy(() => import("./StandardScoresGraph"));
const ScoreStatements = React.lazy(() => import("./ScoreStatements"));
const CandyBearAnalysisGraph = React.lazy(() => import("./CandyBearAnalysisGraph"));
const CandyBearAnalysisGraphClassComparison = React.lazy(() => import("./CandyBearAnalysisGraphClassComparison"));
const CandyBearAnalysisGraphClassAverage = React.lazy(() => import("./CandyBearAnalysisGraphClassAverage"));
const ScoreInterpretationTable = React.lazy(() => import("./ScoreInterpretationTable"));
const TaskDetailChart = React.lazy(() => import("./TaskDetailChart"));
const StudentPerformancePrintViewAlphabetical = React.lazy(() => import("./StudentPerformancePrintViewAlphabetical"));
const VideoModal = React.lazy(() =>
  import("../components/VideoModal")
);

const StandardScoreInterpretationTable = React.lazy(() => import("./StandardScoreInterpretationTable"));


function SencoStudentsResultsOverview(props) {
  // ============= PROPS DESTRUCTURING =============
  const {
    toggleScoreType,
    showAtRiskOnly,
    schoolid,
    teacherid,
    classIds,
    allClassesAssessments,
    assessment,
    classList,
    assessmentId,
    academicYear
  } = props;

  // ============= CORE STATE =============
  const [isLoading, setIsLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [classesResults, setClassesResults] = useState({});
  const [loadingClassId, setLoadingClassId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // ============= DATA STATE =============
  const [classDetails, setClassDetails] = useState(null);
  const [activeAccordionClassId, setActiveAccordionClassId] = useState(null);
  const [classAssessments, setClassAssessments] = useState(null);
  const [studentResults, setStudentResults] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState(null);
  const [gradeLevel, setGradeLevel] = useState(null);
  const [assessmentResultsByStudent, setAssessmentResultsByStudent] = useState([]);
  const [readingScores, setReadingScores] = useState([]);
  const [loadedClassDetailsId, setLoadedClassDetailsId] = useState(null);
  const [loadedClassAssessmentsId, setLoadedClassAssessmentsId] = useState(null);
  const [countryId, setCountryId] = useState(null);

  // ============= UI STATE =============
  const [activeTab, setActiveTab] = useState("overview");
  const [activeAccordionKey, setActiveAccordionKey] = useState(null);
  const [activeNotScreenedKey, setActiveNotScreenedKey] = useState("0");
  const [activeAccordionStudents, setActiveAccordionStudents] = useState([]);
  const [activeClassName, setActiveClassName] = useState("");
  const [openCard, setOpenCard] = useState(null);

  // ============= TASK/SELECTION STATE =============
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedChildID, setSelectedChildID] = useState("");
  const [individualStudentResult, setIndividualStudentResult] = useState("");
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedTaskData, setSelectedTaskData] = useState(null);
  const [isTaskDetailVisible, setIsTaskDetailVisible] = useState(false);

  // ============= PRINT STATE =============
  const [showPrintView, setShowPrintView] = useState(false);
  const [showStudentPerformancePrintView, setShowStudentPerformancePrintView] = useState(false);
  const [showChildPrintOptions, setShowChildPrintOptions] = useState(false);
  const [progressGraphLoaded, setProgressGraphLoaded] = useState(false);
  const [allStudentsAccordionKey, setAllStudentsAccordionKey] = useState("all-students");
  const [activeClassAverageSubTab, setActiveClassAverageSubTab] = useState("classAverageComparison");
  const [activeProgressSubTab, setActiveProgressSubTab] = useState("screeningProgressComparison");

  // ============= PROGRESS/ACCORDION STATE =============
  const [openedAccordions, setOpenedAccordions] = useState(new Set());

  // ============= ACTION CARD STATE =============
  const [accordionSelectedClass, setAccordionSelectedClass] = useState(null);
  const [accordionCardPosition, setAccordionCardPosition] = useState({ top: 0, left: 0 });
  const [classTaskList, setClassTaskList] = useState([]);
  const [selectedClassTasks, setSelectedClassTasks] = useState([]);
  const [selectAllClassTasks, setSelectAllClassTasks] = useState(false);
  const [showClassTaskList, setShowClassTaskList] = useState(false);
  const [showClassDeleteModal, setShowClassDeleteModal] = useState(false);
  const [classTaskToDelete, setClassTaskToDelete] = useState(null);

  // ============= CARD POSITION STATE =============
  const [shareCardPosition, setShareCardPosition] = useState({ top: 0, left: 0 });
  const [printActionCardPosition, setPrintActionCardPosition] = useState({ top: 0, left: 0 });
  const [printCardPosition, setPrintCardPosition] = useState({ top: 0, left: 0 });

  // ============= VIEW STATE =============
  const [viewParent, setViewParent] = useState(false);
  const [viewTeacher, setViewTeacher] = useState(false);

  const [localToggleScoreType, setLocalToggleScoreType] = useState(false);

  // ============= REGION STATE =============
  const [showRegionLockModal, setShowRegionLockModal] = useState(false);
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [countryStandardisedScoresEnabled, setCountryStandardisedScoresEnabled] = useState(false);
  const [standardisedScoresEnabled, setStandardisedScoresEnabled] = useState(false);
  const [attemptedTab, setAttemptedTab] = useState(null);

  // ============= DYNAMIC ASSESSMENT ID STATE =============
  const [assessmentIdsByGrade, setAssessmentIdsByGrade] = useState(null);


  // ============= REFS =============
  const navigate = useNavigate();
  const activeAccordionRef = useRef(null);
  const performanceBodyRef = useRef(null);
  const recommendationsBodyRef = useRef(null);
  const planningSetBodyRef = useRef(null);
  const allStudentsBodyRef = useRef(null);
  const individualChildBodyRef = useRef(null);
  const classAvgComparisonBodyRef = useRef(null);
  const classAvgClassBodyRef = useRef(null);
  const screeningPointBodyRef = useRef(null);
  const classroomProgressBodyRef = useRef(null);
  const screeningStatusRef = useRef(null);
  const notScreenedBodyRef = useRef(null);
  const accordionActionCardRef = useRef(null);
  const classRefs = useRef({});
  const individualChildRef = useRef();
  const candyBearRef = useRef();
  const standardScoreGraphRef = useRef();

  // ============= CONSTANTS =============
  const isStaging = window.location.href.includes("staging") ||
    window.location.href.includes("localhost") ||
    window.location.href.includes("alpaca-assessment-test");

  const fromSencoResults = true;

  // ============= DEBUGGING =============
  useEffect(() => {
    console.log('ClassesResults changed:', Object.keys(classesResults));
  }, [classesResults]);

  useEffect(() => {
    console.log('IsLoading changed:', isLoading);
  }, [isLoading]);

  useEffect(() => {
    console.log('AssessmentResultsByStudent changed:', assessmentResultsByStudent.length);
  }, [assessmentResultsByStudent]);

  const academicYearLabel = academicYear?.display || "Unknown Year";
  const academicYearTimeframe = academicYear ? {
    start: new Date(academicYear.start).toISOString(),
    end: new Date(academicYear.end).toISOString(),
  } : null;

  useEffect(() => {
    console.log('=== SencoStudentsResultsOverview Props ===');
    console.log('academicYear from props:', academicYear);
  }, [academicYear]);

  // ============= UTILITY FUNCTIONS =============
  const hasResults = (classResults) => {
    if (!classResults || !classResults.students) {
      return false;
    }

    const students = Object.values(classResults.students);

    // Check if any student has results with actual data
    return students.some(student =>
      student.results &&
      student.results.length > 0 &&
      student.results.some(result =>
        result.groupedTasks &&
        result.groupedTasks.length > 0
      )
    );
  };

  const getProgressTabStatus = () => {
    const now = new Date();
    const irishTime = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Dublin" }));
    const currentHour = irishTime.getHours();
    const currentMinute = irishTime.getMinutes();
    const dayOfWeek = irishTime.getDay();

    const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
    const isAvailable = !isWeekday ||
      (currentHour > 13) ||
      (currentHour === 13 && currentMinute >= 30);

    return {
      isAvailable,
      currentTime: irishTime.toLocaleTimeString("en-IE", {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }),
      dayOfWeek: irishTime.toLocaleDateString("en-IE", { weekday: 'long' })
    };
  };

  const toggleCard = (cardName) => {
    setOpenCard((prevCard) => (prevCard === cardName ? null : cardName));
  };

  const thresholds = {
    "Rhyming": { task: 20, student: 25 },
    "Initial Phoneme": { task: 20, student: 20 },
    "Phoneme Blending": { task: 20, student: 20 },
    "Letter Name": { task: 20, student: 15 },
    "Deletion": { task: 20, student: 20 },
    "Letter Sound": { task: 20, student: 20 },
    "Word Reading": { task: 10, student: 15 },
    "Nonword Reading": { task: 10, student: 15 },
    "Non Word Reading": { task: 10, student: 15 },
    "Phonological Awareness": { task: 10, student: 15 },
    "Spelling": { task: 20, student: 20 },
    "Comprehension & Expressive Vocab": { task: 20, student: 20 },
    "Alphabetic Knowledge": { task: 40, student: 20 },
    "Alphabetic Knowledge (a)": { task: 40, student: 20 },
  };

  const planningSetThresholds = {
    "Rhyming": { task: 20, student: 20 },
    "Initial Phoneme": { task: 20, student: 20 },
    "Phoneme Blending": { task: 20, student: 20 },
    "Letter Name": { task: 20, student: 20 },
    "Deletion": { task: 20, student: 20 },
    "Letter Sound": { task: 20, student: 20 },
    "Word Reading": { task: 20, student: 20 },
    "Nonword Reading": { task: 20, student: 20 },
    "Non Word Reading": { task: 20, student: 20 },
    "Phonological Awareness": { task: 20, student: 20 },
    "Spelling": { task: 20, student: 20 },
    "Comprehension & Expressive Vocab": { task: 20, student: 20 },
    "Alphabetic Knowledge": { task: 20, student: 20 },
    "Alphabetic Knowledge (a)": { task: 20, student: 20 },
  };

  const determineGradeLevel = (classDetails) => {
    if (classDetails?.grade_level3) return "first";
    if (classDetails?.grade_level2) return "senior";
    return "junior"; // grade_level1 or default
  };

  const gradeLevelKey = determineGradeLevel(classDetails);
  const selectedAssessmentIds = assessmentIdsByGrade?.[gradeLevelKey] ?? null;

  // ============= MEMOIZED VALUES =============
  const groupedResults = useMemo(
    () => groupAndAverageTasks(assessmentResultsByStudent),
    [assessmentResultsByStudent]
  );

  const taskNameArray = useMemo(() => {
    const taskNames = new Set();
    groupedResults.forEach(({ tasks }) => {
      tasks?.forEach(({ name }) => taskNames.add(name));
    });
    return [...taskNames];
  }, [groupedResults]);

  // ============= DATA TRANSFORMATION =============
  const transformClassesToStudentResults = useCallback(() => {
    console.log('transformClassesToStudentResults called, classesResults keys:', Object.keys(classesResults));

    if (!classesResults || Object.keys(classesResults).length === 0) {
      return [];
    }

    const transformedResults = [];

    Object.values(classesResults).forEach((classObj) => {
      if (!classObj || typeof classObj !== 'object') {
        console.warn('Invalid classObj:', classObj);
        return;
      }

      const students = classObj.students || {};

      if (!students || typeof students !== 'object') {
        console.warn('Invalid students object:', students);
        return;
      }

      Object.entries(students).forEach(([studentId, student]) => {
        if (!student || typeof student !== 'object') {
          console.warn('Invalid student object:', student);
          return;
        }

        (student.results || []).forEach((result) => {
          if (!result || typeof result !== 'object') {
            console.warn('Invalid result object:', result);
            return;
          }

          if (!result.tasks || result.tasks.length === 0) {
            return;
          }

          const transformedResult = {
            assessmentInstance: result.id,
            dateAssigned: result.date,
            completed: true,
            student: {
              name: student.name || 'Unknown',
              id: studentId,
              sen: student.sen || 'Unknown',
            },
            time: result.tasks.reduce((total, task) => total + (task.duration || 0), 0),
            tasks: result.tasks.map(task => ({
              answerid: task.answerid,
              name: task.name,
              order: task.order,
              parentTaskid: task.parentTaskid,
              parentTaskName: task.parentTaskName,
              score: task.score,
              zScore: task.zScore,
              standardScore: task.standardScore,
              sTenScore: task.sTenScore,
              groupPercentile: task.groupPercentile,
              globalPercentile: 0,
              nationalAverageStandardScore: task.nationalAverageStandardScore,
            })),
          };

          transformedResults.push(transformedResult);
        });
      });
    });

    return transformedResults;
  }, []);

  const formattedClassData = useMemo(() => {
    if (!classList) return [];
    const initialData = classList
      .filter(({ classId, name }) => classId && name)
      .reduce(
        (acc, { classId, name }) => ({
          ...acc,
          [classId]: {
            id: classId,
            name,
            taskScores: {},
            assessmentResults: [],
          },
        }),
        {}
      );

    Object.entries(classesResults).forEach(([classId, classObj]) => {
      if (!classId || !classObj || !initialData[classId]) {
        return;
      }

      const students = classObj.students || {};
      const classTaskScores = {};

      Object.entries(students).forEach(([studentId, student]) => {
        student.results?.forEach((result) => {
          result.groupedTasks?.forEach((group) => {
            if (!group.name || !group.aggregated) {
              return;
            }

            const taskName = group.name;
            const score = toggleScoreType
              ? group.aggregated.groupPercentile
              : group.aggregated.score * 100;

            if (!classTaskScores[taskName]) {
              classTaskScores[taskName] = [];
            }
            classTaskScores[taskName].push(score);
          });
        });
      });

      Object.keys(classTaskScores).forEach((taskName) => {
        const scores = classTaskScores[taskName];
        if (scores.length > 0) {
          const averageScore =
            scores.reduce((a, b) => a + b, 0) / scores.length;
          classTaskScores[taskName] = averageScore.toFixed(2);
        } else {
          delete classTaskScores[taskName];
        }
      });

      const validAssessmentResults = Object.entries(students)
        .map(([studentId, student]) => {
          const tasks =
            student.results?.flatMap((result) => result.groupedTasks || []) ||
            [];
          const studentObj = { ...student, id: studentId };
          return {
            student: studentObj,
            tasks,
          };
        })
        .filter(({ student, tasks }) => student.id && tasks.length > 0);

      initialData[classId] = {
        ...initialData[classId],
        taskScores: classTaskScores,
        assessmentResults: validAssessmentResults,
      };
    });

    const filteredData = Object.values(initialData).filter(
      (data) => data.id && data.name
    );

    return filteredData;
  }, [classList, classesResults, toggleScoreType]);

  const allStudentsResults = useMemo(() => {
    return Object.values(classesResults)
      .flatMap((classObj) => {
        return Object.entries(classObj.students || {}).map(([id, student]) => {
          const studentData = {
            student: { name: student.name, id: id },
            tasks: student.results
              ? student.results.flatMap((result) => {
                return result.tasks || [];
              })
              : [],
          };

          return studentData;
        });
      })
      .filter((student) => {
        return student.tasks.length > 0;
      });
  }, [classesResults]);

  const averageStandardScoresPerTask = useMemo(() => {
    const taskScores = {};

    Object.values(classesResults).forEach((classObj) => {
      Object.entries(classObj.students || {}).forEach(([id, student]) => {
        student.results?.forEach((result) => {
          result.groupedTasks?.forEach((group) => {
            if (!group.name || !group.aggregated) return;

            if (!taskScores[group.name]) {
              taskScores[group.name] = [];
            }

            if (
              group.aggregated.standardScore !== undefined &&
              !isNaN(group.aggregated.standardScore)
            ) {
              taskScores[group.name].push(group.aggregated.standardScore);
            }
          });
        });
      });
    });

    const averageStandardScores = Object.keys(taskScores).map((taskName) => {
      const scores = taskScores[taskName];
      if (scores.length === 0) {
        return { taskName, averageStandardScore: 0 };
      }
      const sum = scores.reduce((sum, score) => sum + score, 0);
      const averageStandardScore = sum / scores.length;
      return { taskName, averageStandardScore };
    });

    return averageStandardScores;
  }, [classesResults]);

  const allClassStudents = useMemo(() => {
    const studentList = [];

    Object.entries(classesResults).forEach(([classId, classObj]) => {
      const students = classObj.students || {};

      Object.entries(students).forEach(([studentId, student]) => {
        studentList.push({
          id: studentId,
          name: student.name || "Unknown",
          classId: classId,
        });
      });
    });

    return studentList;
  }, [classesResults]);

  // ============= EVENT HANDLERS =============
  const handleAccordionActionClick = (event, classId, className, index) => {
    event.stopPropagation();
    event.preventDefault();

    setActiveAccordionKey(String(classId));
    setActiveClassName(className);
    setActiveAccordionClassId(classId);

    if (!classesResults[classId]) {
      handleClick(classId);
      return;
    }

    const classStudents = classesResults[classId]?.students || {};
    const studentIds = Object.keys(classStudents);

    if (studentIds.length === 0) {
      console.warn("No students found for class:", classId);
      return;
    }

    setAccordionSelectedClass({
      id: studentIds,
      name: className,
      classId: classId,
    });

    const rect = event.currentTarget.getBoundingClientRect();
    setAccordionCardPosition({
      top: rect.top + window.scrollY,
      left: rect.left - 300,
    });

    const classTasks = extractClassTasks(classId);
    setClassTaskList(classTasks);

    setSelectedClassTasks([]);
    setSelectAllClassTasks(false);
    setShowClassTaskList(false);

    const students = classesResults[classId].students;
    const studentArray = Object.entries(students).map(([id, student]) => ({
      id,
      name: student.name,
    }));
    setActiveAccordionStudents(studentArray);
  };

  const extractClassTasks = (classId) => {
    if (!classesResults[classId] || !classesResults[classId].students) {
      return [];
    }

    const taskMap = new Map();
    const processedAnswerIds = new Set();

    const students = classesResults[classId].students;
    Object.values(students).forEach((student) => {
      student.results?.forEach((result) => {
        if (result.originalTasks) {
          result.originalTasks.forEach((task) => {
            if (!processedAnswerIds.has(task.answerid)) {
              processedAnswerIds.add(task.answerid);

              if (!taskMap.has(task.name)) {
                taskMap.set(task.name, {
                  name: task.name,
                  answerIds: [task.answerid],
                });
              } else {
                taskMap.get(task.name).answerIds.push(task.answerid);
              }
            }
          });
        }

        result.groupedTasks?.forEach((group) => {
          group.tasks?.forEach((task) => {
            if (!processedAnswerIds.has(task.answerid)) {
              processedAnswerIds.add(task.answerid);

              if (!taskMap.has(task.name)) {
                taskMap.set(task.name, {
                  name: task.name,
                  answerIds: [task.answerid],
                });
              } else {
                taskMap.get(task.name).answerIds.push(task.answerid);
              }
            }
          });
        });
      });
    });

    return Array.from(taskMap.values());
  };

  const handleClassTaskSelection = (taskName) => {
    setSelectedClassTasks((prev) => {
      const updatedTasks = prev.includes(taskName)
        ? prev.filter((name) => name !== taskName)
        : [...prev, taskName];

      setSelectAllClassTasks(updatedTasks.length === classTaskList.length);
      return updatedTasks;
    });
  };

  const handleSelectAllClassTasks = () => {
    if (selectAllClassTasks) {
      setSelectedClassTasks([]);
    } else {
      setSelectedClassTasks(classTaskList.map((task) => task.name));
    }
    setSelectAllClassTasks(!selectAllClassTasks);
  };

  const handleClassRetake = () => {
    if (selectedClassTasks.length === 0) {
      alert("Please select at least one task to retake.");
      return;
    }

    const allAnswerIds = [
      ...new Set(
        classTaskList
          .filter((task) => selectedClassTasks.includes(task.name))
          .flatMap((task) => task.answerIds)
      ),
    ];

    const isFullAssessment =
      selectAllClassTasks || selectedClassTasks.length === classTaskList.length;

    const taskName = isFullAssessment
      ? "Full Assessment"
      : selectedClassTasks.join(", ");

    setClassTaskToDelete({
      answerIds: allAnswerIds,
      taskName: taskName,
      isFullAssessment: isFullAssessment,
      className: accordionSelectedClass.name,
    });

    setShowClassDeleteModal(true);
    toggleCard("deleteModal");
  };

  const handleClassDeleteSuccess = (taskName, reason) => {
    const classId = accordionSelectedClass?.id;
    const className = accordionSelectedClass?.name;

    handleCloseAccordionCard();
    setShowClassDeleteModal(false);
    toggleCard(null);

    if (classId) {
      setTimeout(() => {
        reloadClassData(classId);
      }, 300);
    }
  };

  const handleCloseAccordionCard = () => {
    setAccordionSelectedClass(null);
    toggleCard(null);
  };

  // Used by all secondary-tab accordions to keep activeClassName in sync
  const handleSecondaryAccordionSelect = (key) => {
    setActiveAccordionKey(key);
    if (key) {
      const classData = formattedClassData.find((c) => String(c.id) === String(key));
      setActiveClassName(classData?.name || "");
    } else {
      setActiveClassName("");
    }
  };

  const handleAccordionToggle = (key, ref, className, classId) => {
    const newActiveKey = key === activeAccordionKey ? null : key;
    setActiveAccordionKey(newActiveKey);

    if (newActiveKey) {
      activeAccordionRef.current = ref;
      setActiveClassName(className);
      setActiveAccordionClassId(classId);

      const students = classesResults[classId]?.students || {};
      const studentArray = Object.entries(students).map(([id, student]) => ({
        id,
        name: student.name,
      }));
      setActiveAccordionStudents(studentArray);
    } else {
      setActiveClassName("");
      setActiveAccordionClassId(null);
      setActiveAccordionStudents([]);
    }
  };

  const handleTaskClick = (taskName) => {
    const taskData = formattedClassData
      .map((classData) => ({
        className: classData.name,
        score: classData.taskScores[taskName],
      }))
      .filter((data) => data.score !== undefined);

    if (selectedTask === taskName) {
      setIsTaskDetailVisible(!isTaskDetailVisible);
      setSelectedTask(null);
      setSelectedTaskData(null);
    } else {
      setSelectedTask(taskName);
      setSelectedTaskData(taskData);
      setIsTaskDetailVisible(taskData.length > 0);
    }
  };

  // ============= HANDLE CLICK FUNCTION =============
  const handleClick = async (classId) => {
    if (classesResults[classId] || loadingClassId === classId) {
      console.log(`Skipping duplicate call for class ${classId}`);
      return;
    }

    console.log(`Making API call for class ${classId}`);
    setLoadingClassId(classId);

    try {
      const response = await getClassesAssessmentResults(
        [classId],
        assessment.id
      );

      const groupedResults = groupClassResults(response);

      setClassesResults((prevResults) => {
        const newResults = {
          ...prevResults,
          ...groupedResults,
        };
        return newResults;
      });

      if (!initialLoadComplete) {
        setInitialLoadComplete(true);
        setIsLoading(false);
      }
    } catch (error) {
      console.error(`Error fetching data for class ${classId}:`, error);
    } finally {
      setLoadingClassId(null);
    }
  };

  const handleAnswerDeleted = (classId) => {
    reloadClassData(classId);
  };

  const reloadClassData = async (classId) => {
    setLoadingClassId(classId);

    try {
      const response = await getClassesAssessmentResults(
        [classId],
        assessment.id
      );

      const groupedResults = groupClassResults(response);

      setClassesResults((prevResults) => {
        const newResults = { ...prevResults };

        if (groupedResults[classId]) {
          newResults[classId] = groupedResults[classId];
        }

        return newResults;
      });

      setRefreshTrigger((prev) => prev + 1);
      setActiveAccordionKey(classId.toString());
    } catch (error) {
      console.error(`Error fetching data for class ${classId}:`, error);
    } finally {
      setLoadingClassId(null);
    }
  };

  // ============= NAVIGATION FUNCTIONS =============
  const toAllStudentResults = (selectedStudentIds, viewParent, viewTeacher) => {
    console.log('=== toAllStudentResults Called ===');
    console.log('selectedStudentIds:', selectedStudentIds);
    console.log('viewParent:', viewParent);
    console.log('viewTeacher:', viewTeacher);

    console.log('\n=== Available Data ===');
    console.log('studentResults:', studentResults);
    console.log('studentResults type:', typeof studentResults);
    console.log('studentResults length:', studentResults?.length);

    if (studentResults && studentResults.length > 0) {
      console.log('First studentResult:', studentResults[0]);
      console.log('Has results?', !!studentResults[0]?.results);
      if (studentResults[0]?.results?.[0]) {
        console.log('First result in first student:', studentResults[0].results[0]);
        console.log('Has originalTasks?', !!studentResults[0].results[0].originalTasks);
        console.log('Has answers in first task?', !!studentResults[0].results[0].originalTasks?.[0]?.answers);
      }
    }

    console.log('\ngroupedResults:', groupedResults);
    console.log('classDetails:', classDetails);
    console.log('assessment:', assessment);

    console.log('\n=== Filtered Students ===');
    const filteredStudents = students.filter((student) =>
      selectedStudentIds.includes(student.id)
    );
    console.log('Filtered students:', filteredStudents);

    setViewParent(viewParent);
    setViewTeacher(viewTeacher);

    // Transform into format AllResultsHistory expects
    // Structure: { [studentId]: [{ name, tasks, originalTasks, studentName }] }
    // PRIORITY: Use studentResults (from detailed API with answers) over classesResults
    const studentResultsForHistory = {};

    // Get student name lookup from classesResults
    const studentNameLookup = {};
    Object.values(classesResults).forEach((classObj) => {
      if (!classObj?.students) return;
      Object.entries(classObj.students).forEach(([studentId, student]) => {
        studentNameLookup[studentId] = student.name || 'Unknown';
      });
    });

    // Check if we have detailed results from API (studentResults has the answers)
    const hasDetailedResults = studentResults && typeof studentResults === 'object' && Object.keys(studentResults).length > 0;
    console.log('=== toAllStudentResults Data Source ===');
    console.log('Has detailed studentResults?', hasDetailedResults);
    console.log('studentResults:', studentResults);

    if (hasDetailedResults) {
      // Use studentResults (from getAllStudentsResultsPerAssessment API - has full answers)
      selectedStudentIds.forEach((studentId) => {
        const results = studentResults[studentId];
        if (!results || !Array.isArray(results)) return;

        studentResultsForHistory[studentId] = [];

        results.forEach((result) => {
          const tasks = result.tasks || [];
          if (tasks.length === 0) return;

          // Debug: Check answers
          console.log('=== Detailed API Task Answers ===');
          console.log('Result name:', result.name);
          console.log('Tasks count:', tasks.length);
          if (tasks[0]) {
            console.log('First task has answers?', !!tasks[0].answers);
            console.log('First task answers length:', tasks[0].answers?.length);
          }

          studentResultsForHistory[studentId].push({
            name: result.name || assessment?.name || 'Assessment',
            id: result.id,
            date: result.date,
            tasks: tasks,
            originalTasks: tasks,
            studentName: studentNameLookup[studentId] || 'Unknown',
          });
        });
      });
    } else {
      // Fallback to classesResults (may have empty answers)
      console.log('Falling back to classesResults (no detailed studentResults)');
      Object.values(classesResults).forEach((classObj) => {
        if (!classObj?.students) return;

        Object.entries(classObj.students).forEach(([studentId, student]) => {
          if (!selectedStudentIds.includes(studentId)) return;

          if (!studentResultsForHistory[studentId]) {
            studentResultsForHistory[studentId] = [];
          }

          (student.results || []).forEach((result) => {
            const tasksWithAnswers = result.originalTasks || result.tasks;
            if (!tasksWithAnswers || tasksWithAnswers.length === 0) return;

            studentResultsForHistory[studentId].push({
              name: result.name || assessment?.name || 'Assessment',
              id: result.id,
              date: result.date,
              tasks: tasksWithAnswers,
              originalTasks: tasksWithAnswers,
              studentName: student.name || 'Unknown',
            });
          });
        });
      });
    }

    console.log('studentResultsForHistory:', studentResultsForHistory);

    const validAssessmentInstances = (assessment.instances || []).filter((id) =>
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
        id
      )
    );

    const state = {
      studentResults,
      studentResultsForHistory,
      classDetails,
      classAssessments,
      gradeLevel,
      teachers,
      assessment: {
        ...assessment,
        instances: validAssessmentInstances,
      },
      taskNameArray,
      assessmentResultsByStudent: groupedResults,
      toggleScoreType,
      schoolid,
      teacherid,
      viewParent,
      viewTeacher,
      fromSencoResults: true,
      students: filteredStudents,
      readingScores,
    };

    console.log('\n=== Final State Being Passed ===');
    console.log('state:', state);

    navigate("/allstudents/results", {
      state,
    });
  };

  const toReportCards = (selectedStudentIds) => {
    navigate("/allstudents/reportcards", {
      state: {
        studentResults,
        classDetails,
        classAssessments,
        gradeLevel,
        teachers,
        assessment,
        taskNameArray,
        assessmentResultsByStudent: groupedResults,
        toggleScoreType,
        schoolid,
        teacherid,
        students: students.filter((student) =>
          selectedStudentIds.includes(student.id)
        ),
      },
    });
  };

  // ============= API FUNCTIONS =============
  const fetchTeachers = async () => {
    try {
      const response = await getTeachers(schoolid);
      setTeachers(response);
    } catch (error) {
      console.error("Error fetching teachers:", error);
    }
  };

  const fetchIndividualStudentResults = async (studentId) => {
    try {
      const response = await getIndividualStudentResults(studentId);
      setIndividualStudentResult(response);
    } catch (error) {
      console.error("Error fetching individual student results:", error);
    }
  };

  // ============= MAIN EFFECTS =============
  useEffect(() => {
    fetchTeachers();
  }, [schoolid]);

  useEffect(() => {
    if (selectedChildID) {
      setProgressGraphLoaded(false);
      setShowChildPrintOptions(false);
      fetchIndividualStudentResults(selectedChildID);
    }
  }, [selectedChildID]);

  useEffect(() => {
    if (Array.isArray(individualStudentResult) && individualStudentResult.length > 0) {
      setProgressGraphLoaded(true);
    }
  }, [individualStudentResult]);

  useEffect(() => {
    if (schoolid) {
      getCountryId(schoolid)
        .then((response) => {
          if (response.countryId) {
            setCountryId(response.countryId);
          } else {
            setCountryId(null);
          }
        })
        .catch((error) => {
          console.error("Failed to fetch country ID:", error);
          setCountryId(null);
        });
    }
  }, [schoolid]);

  useEffect(() => {
    if (!schoolid) return;

    const gradeLevelKeyMap = { '1': 'junior', '2': 'senior', '3': 'first' };

    getScreenerAssessmentIds(schoolid)
      .then(data => {
        const mapping = {};
        Object.entries(data).forEach(([level, points]) => {
          const key = gradeLevelKeyMap[level];
          if (key) mapping[key] = points;
        });
        setAssessmentIdsByGrade(mapping);
      })
      .catch((error) => {
        console.error('Failed to fetch screener assessment IDs:', error);
        setAssessmentIdsByGrade({});
      });
  }, [schoolid]);

  useEffect(() => {
    console.log('Effect: transforming classes to student results');
    const results = transformClassesToStudentResults();
    if (results.length > 0) {
      setAssessmentResultsByStudent(results);
    }
  }, [classesResults, transformClassesToStudentResults]);

  useEffect(() => {
    if (formattedClassData.length > 0) {
      const classId = formattedClassData[0].id;

      if (classId !== loadedClassDetailsId && !classDetails) {
        console.log("Loading class details for:", classId);
        setLoadedClassDetailsId(classId);

        getClass(classId).then((response) => {
          setClassDetails(response);
          setGradeLevel(response.grade_level);
        }).catch(error => {
          console.error("Error fetching class details:", error);
          setLoadedClassDetailsId(null);
        });
      }
    }
  }, [formattedClassData, loadedClassDetailsId, classDetails]);

  useEffect(() => {
    if (formattedClassData.length > 0) {
      const classId = formattedClassData[0].id;

      if (classId !== loadedClassAssessmentsId && !classAssessments) {
        console.log("Loading class assessments for:", classId);
        setLoadedClassAssessmentsId(classId);

        getClassAssessments(classId).then((response) => {
          setClassAssessments(response);
        }).catch(error => {
          console.error("Error fetching class assessments:", error);
          setLoadedClassAssessmentsId(null);
        });
      }
    }
  }, [formattedClassData, loadedClassAssessmentsId, classAssessments]);

  useEffect(() => {
    if (classList !== null && classList.length === 0) {
      setIsLoading(false);
      setInitialLoadComplete(true);
    }
  }, [classList]);

  useEffect(() => {
    if (classList && classList.length > 0 && !activeAccordionKey) {
      const firstClassId = classList[0].classId;
      const firstClassName = classList[0].name;

      console.log("Initializing first class:", firstClassId);

      setActiveAccordionKey(String(firstClassId));
      setActiveClassName(firstClassName);
      setActiveAccordionClassId(firstClassId);

      if (!classesResults[firstClassId]) {
        setIsLoading(true);
        handleClick(firstClassId);
      } else {
        console.log("Data already exists for first class, skipping API call");
        setIsLoading(false);
        setInitialLoadComplete(true);
      }

      if (activeTab === 'recommendations' || activeTab === 'performance') {
        classList.forEach((classItem) => {
          if (!classesResults[classItem.classId]) {
            handleClick(classItem.classId);
          }
        });
      }
    }
  }, [classList, activeTab]);

  useEffect(() => {
    if (
      activeAccordionClassId &&
      classesResults[activeAccordionClassId]?.students
    ) {
      const students = classesResults[activeAccordionClassId].students;
      const studentArray = Object.entries(students).map(([id, student]) => ({
        id,
        name: student.name,
      }));
      setActiveAccordionStudents(studentArray);
    }
  }, [classesResults, activeAccordionClassId, refreshTrigger]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        accordionActionCardRef.current &&
        !accordionActionCardRef.current.contains(event.target)
      ) {
        handleCloseAccordionCard();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.warn('Loading timeout reached, forcing completion');
        setIsLoading(false);
        setInitialLoadComplete(true);
      }
    }, 30000);

    return () => clearTimeout(timeout);
  }, [isLoading]);

  useEffect(() => {
    const fetchCountryStandardisedScoresStatus = async () => {
      if (!countryId) return;

      try {
        const tags = await getEntityTags('country', countryId);
        const tagDefs = await getTagDefinitions();

        const countryScoreDef = tagDefs.find(
          def => def.tag_name === 'country_standardised_scores_enabled'
        );

        if (countryScoreDef) {
          const countryTag = tags.find(
            tag => tag.tag_definition_id === countryScoreDef.id
          );
          const enabled = countryTag?.tag_value === 'ON';
          setCountryStandardisedScoresEnabled(enabled);
        }
      } catch (error) {
        console.error('Error checking country tag:', error);
        setCountryStandardisedScoresEnabled(false);
      }
    };

    fetchCountryStandardisedScoresStatus();
  }, [countryId]);

  useEffect(() => {
    if (!assessmentId) return;
    checkStandardisedScoresEnabled(assessmentId)
      .then(enabled => setStandardisedScoresEnabled(enabled))
      .catch(() => setStandardisedScoresEnabled(false));
  }, [assessmentId]);

  const isMainLoading = isLoading && !initialLoadComplete;


  const handleTabSelect = (tabKey) => {
    const blockedTabs = ["recommendations", "results3", "results4"];

    if (blockedTabs.includes(tabKey) && !countryStandardisedScoresEnabled) {
      const tabNames = {
        "recommendations": "Insights",
        "results3": "Individual Child",
        "results4": "Class Average",
      };

      setAttemptedTab(tabNames[tabKey] || "This feature");
      setShowRegionLockModal(true);
      return;
    }

    if (blockedTabs.includes(tabKey) && !standardisedScoresEnabled) {
      setShowUnavailableModal(true);
      return;
    }

    setActiveTab(tabKey);
    setShowChildPrintOptions(false);
  };

  return isMainLoading ? (
    <div className="loading-container">
      <div className="spinner-container">
        <div className="big-ball-spinner"></div>
        <p>Loading data, please wait...</p>
      </div>
    </div>
  ) : (
    <>
      <Row className="inline-block align-items-center push-down mt-4">
        <Col className="col-auto">
          <Suspense fallback={<div>Loading Navigation Button...</div>}>
            <ReturnToSencoResults />
          </Suspense>
        </Col>
        <Col className="flex-grow-1 text-center" style={{ margin: "10px" }}>
          <h2 className="page-description-header-results">
            SENCO Results Overview
          </h2>
        </Col>
        <Col
          className="col-auto d-flex justify-content-end align-items-center"
          style={{ gap: "16px" }}
        >
          <Suspense fallback={<div>Loading SEN Watchlist Button...</div>}>
            <SENWatchlistButton
              schoolid={schoolid}
              teachers={teachers}
              teacherid={teacherid}
              assessment={assessment}
              readingScores={readingScores}
            />
          </Suspense>
          <button
            className="print-button"
            style={{ whiteSpace: "nowrap" }}
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              setPrintCardPosition({
                top: rect.top + window.scrollY,
                left: rect.left - 250,
              });
              toggleCard("printCard");
            }}
          >
            {assessment.name?.match(/\b(Baseline|Midpoint|End)\b/i)?.[0] ||
              assessment.name?.split(" ")[1]} Report Cards
            <img
              src={process.env.PUBLIC_URL + "/assets/imgs/chevron-sort.svg"}
              alt="Sort"
              className="print-button-icon"
            />
          </button>

          <button
            className="print-button"
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              setPrintActionCardPosition({
                top: rect.top + window.scrollY,
                left: rect.left - 250,
              });
              toggleCard("printActionCard");
            }}
          >
            Print
            <img
              src={process.env.PUBLIC_URL + "/assets/imgs/chevron-sort.svg"}
              alt="Sort"
              className="print-button-icon"
            />
          </button>
          <button
            className="print-button"
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              setShareCardPosition({
                top: rect.top + window.scrollY,
                left: rect.left - 250,
              });
              toggleCard("shareCard");
            }}
          >
            Share
            <img
              src={process.env.PUBLIC_URL + "/assets/imgs/chevron-sort.svg"}
              alt="Sort"
              className="print-button-icon"
            />
          </button>
        </Col>
      </Row>
      <div className="class-overview-container">
        <div className="senco-results-header">
          {activeClassName}
        </div>
        <div className="bottom-container">
          <Container className="container-shape-senco">
            <Suspense fallback={<div>Loading tabs...</div>}>
              <Tabs
                activeKey={activeTab}
                onSelect={handleTabSelect}
                id="senco-results-tabs"
                className="mb-3"
                mountOnEnter={true}
                unmountOnExit={false}
              >
                <Tab eventKey="overview" title="Class Results">
                  <Row className="page-description-bar align-items-center">
                    <Col className="col-auto">
                      <Suspense
                        fallback={<div>Loading Assessment Dropdown...</div>}
                      >
                        <SencoAssessmentDropdown
                          classAssessments={classAssessments}
                          classIds={classIds}
                          teachers={teachers}
                          assessmentName={assessment.name}
                          assessment={assessment}
                          teacherid={teacherid}
                          schoolid={schoolid}
                          gradeLevel={gradeLevel}
                          allClassesAssessments={allClassesAssessments}
                          assessmentResultsByStudent={
                            assessmentResultsByStudent
                          }
                          fromSencoResults={fromSencoResults}
                          setReadingScores={setReadingScores}
                        />
                      </Suspense>
                    </Col>
                    <Col className="col-1">
                    </Col>
                    <Col className="d-flex justify-content-center align-items-center">
                      <Suspense fallback={null}>
                        <VideoModal
                          buttonText="Understanding Class Results"
                          modalTitle="Understanding Class Results"
                          buttonBackgroundColor="#9D00FF"
                          buttonTextColor="#ffffff"
                          buttonFontSize="75%"
                          buttonWidth="100%"
                          videos={[
                            {
                              title: "Understanding Class Results",
                              iframe: "https://www.youtube.com/embed/Vp5lZdb5en8?si=89JaXHofJFjs3ZlQ"
                            },
                          ]}
                        />
                      </Suspense>
                    </Col>

                    <Col className="d-flex justify-content-end align-items-center">
                      <Suspense
                        fallback={<div>Loading Recommendations...</div>}
                      >
                        <RecommendationsButton onResultsPage={true} assessmentId={assessmentId} />
                      </Suspense>
                      <Suspense fallback={<div>Loading score table...</div>}>
                        {localToggleScoreType ? (
                          <StandardScoreInterpretationTable />
                        ) : (
                          <ScoreInterpretationTable />
                        )}
                      </Suspense>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="col-5"></Col>
                  </Row>
                  {isLoading ? (
                    <div className="spinner-container">
                      <div className="big-ball-spinner"></div>
                    </div>
                  ) : (
                    <Accordion
                      activeKey={activeAccordionKey}
                      onSelect={(key) => setActiveAccordionKey(key)}
                      className="accordion-container"
                    >
                      {classList.map((classItem, index) => {
                        const { classId, name } = classItem;
                        console.log(classItem)
                        return (
                          <Accordion.Item
                            eventKey={String(classId)}
                            key={classId}
                            className="accordion-item-results"
                          >
                            <Accordion.Header
                              onClick={(e) => {
                                if (!e.target.closest(".accordion-actions-icon")) {
                                  handleAccordionToggle(
                                    String(classId),
                                    activeAccordionRef,
                                    name,
                                    classId
                                  );
                                  handleClick(classId);
                                }
                              }}
                              className="accordion-header"
                            >
                              <div className="d-flex justify-content-between w-100 align-items-center">
                                <span>{name}</span>
                                <div className="d-flex align-items-center">
                                  <span
                                    className="accordion-actions-icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();

                                      handleAccordionActionClick(e, classId, name, index);
                                      toggleCard("actionCard");
                                    }}
                                    style={{
                                      padding: "8px",
                                      cursor: "pointer",
                                      zIndex: 10
                                    }}
                                  >
                                    <img
                                      src={process.env.PUBLIC_URL + "/assets/imgs/dots-vertical.svg"}
                                      alt="Actions"
                                      style={{ pointerEvents: "none" }}
                                    />
                                  </span>
                                </div>
                              </div>
                              {loadingClassId === classId && <Fallback />}
                            </Accordion.Header>
                            <Accordion.Body
                              ref={(el) => {
                                if (
                                  activeAccordionKey === String(classId) &&
                                  el
                                ) {
                                  activeAccordionRef.current = el;
                                }
                              }}
                            >
                              {classesResults[classId] ? (
                                console.log('Rendering table for classId:', classId, 'with results:', classesResults[classId]),
                                <Suspense fallback={<div>Loading table..</div>}>
                                  <SencoStudentsResultsTable
                                    classesResults={{
                                      [classId]: classesResults[classId],
                                    }}
                                    // toggleScoreType={props.toggleScoreType}
                                    toggleScoreType={localToggleScoreType}  // ✅ Pass the state
                                    setToggleScoreType={setLocalToggleScoreType}
                                    isPrintOut={false}
                                    showAtRiskOnly={showAtRiskOnly}
                                    schoolid={schoolid}
                                    teacherid={teacherid}
                                    classId={classId}
                                    assessment={assessment}
                                    classDetails={classDetails}
                                    readingScores={readingScores}
                                    classAssessments={classAssessments}
                                    teachers={teachers}
                                    gradeLevel={gradeLevel}
                                    assessmentResultsByStudent={
                                      assessmentResultsByStudent
                                    }
                                    fromSencoResults={fromSencoResults}
                                    setActiveAccordionStudents={
                                      setActiveAccordionStudents
                                    }
                                    activeAccordionStudents={
                                      activeAccordionStudents
                                    }
                                    activeAccordionKey={activeAccordionKey}
                                    setStudentResults={setStudentResults}
                                    setStudents={setStudents}
                                    activeAccordionClassId={
                                      activeAccordionClassId
                                    }
                                    students={students}
                                    onAnswerDeleted={handleAnswerDeleted}
                                    toAllStudentResults={toAllStudentResults}
                                    setViewParent={setViewParent}
                                    setViewTeacher={setViewTeacher}
                                    studentResults={studentResults}
                                    countryId={countryId}
                                    assessmentId={assessmentId}
                                  />
                                </Suspense>
                              ) : (
                                <p>No data loaded yet for this class.</p>
                              )}
                            </Accordion.Body>
                          </Accordion.Item>
                        );
                      })}
                    </Accordion>
                  )}
                </Tab>

                <Tab
                  eventKey="performance"
                  title="Class Analysis"
                  onSelect={(tab) => setActiveTab(tab)}
                  onEnter={() => {
                    classList.forEach((classItem) => {
                      if (!classesResults[classItem.classId]) {
                        handleClick(classItem.classId);
                      }
                    });
                  }}
                >
                  <Row className="page-description-bar align-items-center">
                    <Col className="col-auto">
                      <Suspense
                        fallback={<div>Loading Assessment Dropdown...</div>}
                      >
                        <SencoAssessmentDropdown
                          classAssessments={classAssessments}
                          classIds={classIds}
                          teachers={teachers}
                          assessmentName={assessment.name}
                          assessment={assessment}
                          teacherid={teacherid}
                          schoolid={schoolid}
                          gradeLevel={gradeLevel}
                          allClassesAssessments={allClassesAssessments}
                        />
                      </Suspense>
                    </Col>
                    <Col className="col-1">
                    </Col>
                    <Col className="d-flex justify-content-center align-items-center">
                      <Suspense fallback={null}>
                        <VideoModal
                          buttonText="Understanding Class Analysis"
                          modalTitle="Understanding Class Analysis"
                          buttonBackgroundColor="#9D00FF"
                          buttonTextColor="#ffffff"
                          buttonFontSize="75%"
                          buttonWidth="100%"
                          videos={[
                            {
                              title: "Understanding Class Analysis",
                              iframe: "https://www.youtube.com/embed/8364TC07dDc?si=7D-3Znx0sYJDB2zd"
                            },
                          ]}
                        />
                      </Suspense>
                    </Col>
                    <Col className="d-flex justify-content-end align-items-center">
                      <Suspense
                        fallback={<div>Loading Recommendations...</div>}
                      >
                        <RecommendationsButton onResultsPage={true} assessmentId={assessmentId} />
                      </Suspense>
                      <Suspense fallback={<div>Loading score table...</div>}>
                        <ScoreInterpretationTable />
                      </Suspense>
                    </Col>
                  </Row>
                  <Accordion
                    activeKey={activeAccordionKey}
                    onSelect={handleSecondaryAccordionSelect}
                    className="accordion-container"
                  >
                    {formattedClassData.map((classData) => {
                      const classResults = classesResults[classData.id] || {};
                      const transformedAssessmentResults = Object.values(
                        classResults.students || {}
                      )
                        .map((student) => ({
                          student: student,
                          tasks: (student.results || []).flatMap((result) =>
                            result.groupedTasks.map((group) => ({
                              name: group.name,
                              score: Math.min(
                                Math.max(group.aggregated?.score ?? 0, 0),
                                1
                              ),
                              standardScore:
                                group.aggregated?.standardScore ?? 0,
                              zScore: group.aggregated?.zScore ?? 0,
                              sTenScore: group.aggregated?.sTenScore ?? 0,
                              duration: group.aggregated?.duration ?? 0,
                            }))
                          ),
                        }))
                        .filter((student) => student.tasks.length > 0);
                      return (
                        <Accordion.Item
                          eventKey={String(classData.id)}
                          key={classData.id}
                        >
                          <Accordion.Header
                            onClick={() =>
                              !classesResults[classData.id] &&
                              handleClick(classData.id)
                            }
                          >
                            {classData.name}
                            {loadingClassId === classData.id && <Fallback />}
                          </Accordion.Header>
                          <Accordion.Body
                            ref={(el) => {
                              if (activeAccordionKey === String(classData.id) && el) {
                                performanceBodyRef.current = el;
                              }
                            }}
                          >
                            {classResults ? (
                              <>
                                {hasResults(classResults) ? (
                                  <>
                                    <Suspense fallback={<Fallback />}>
                                      <SencoTaskScoreGraph
                                        tasksData={classData.taskScores}
                                        onLabelClick={handleTaskClick}
                                        className={classData.name}
                                        academicYear={academicYearLabel}
                                        assessmentName={assessment.name}
                                      />
                                    </Suspense>
                                    {selectedTask && selectedTaskData && (
                                      <Suspense fallback={<Fallback />}>
                                        <TaskDetailChart
                                          taskName={selectedTask}
                                          data={selectedTaskData}
                                          className={classData.name}
                                          academicYear={academicYearLabel}
                                        />
                                      </Suspense>
                                    )}
                                    <Suspense fallback={<Fallback />}>
                                      <PieThresholdGraph
                                        assessmentResults={transformedAssessmentResults}
                                        thresholds={thresholds}
                                        fromSencoResults={true}
                                        className={classData.name}
                                      />
                                    </Suspense>
                                    <hr />
                                    {transformedAssessmentResults.length > 0 && (
                                      <Suspense fallback={<Fallback />}>
                                        <ClassroomAnalysis
                                          assessmentResultsByStudent={transformedAssessmentResults}
                                          thresholds={thresholds}
                                        />
                                      </Suspense>
                                    )}
                                  </>
                                ) : (
                                  <div className="no-results-message" style={{
                                    textAlign: 'center',
                                    padding: '2rem',
                                    color: '#6c757d',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.5rem',
                                    margin: '1rem 0'
                                  }}>
                                    <h5>No Results Available</h5>
                                    <p>
                                      There are currently no results available for this screener and class.
                                      Please check back later when this class has finished screening.
                                    </p>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div>Loading data or select this class to load data..</div>
                            )}
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                </Tab>

                <Tab
                  eventKey="recommendations"
                  title="Insights"
                  onSelect={(tab) => setActiveTab(tab)}
                  onEnter={() => {
                    classList.forEach((classItem) => {
                      if (!classesResults[classItem.classId]) {
                        handleClick(classItem.classId);
                      }
                    });
                  }}
                >
                  <Row className="page-description-bar align-items-center">
                    <Col className="col-auto">
                      <Suspense
                        fallback={<div>Loading Assessment Dropdown...</div>}
                      >
                        <SencoAssessmentDropdown
                          classAssessments={classAssessments}
                          classIds={classIds}
                          teachers={teachers}
                          assessmentName={assessment.name}
                          assessment={assessment}
                          teacherid={teacherid}
                          schoolid={schoolid}
                          gradeLevel={gradeLevel}
                          allClassesAssessments={allClassesAssessments}
                        />
                      </Suspense>
                    </Col>
                    <Col className="col-1">
                    </Col>
                    <Col className="d-flex justify-content-center align-items-center">
                      <Suspense fallback={null}>
                        <VideoModal
                          buttonText="Understanding Insights"
                          modalTitle="Understanding Insights"
                          buttonBackgroundColor="#9D00FF"
                          buttonTextColor="#ffffff"
                          buttonFontSize="75%"
                          buttonWidth="100%"
                          videos={[
                            {
                              title: "Understanding Insights",
                              iframe: "https://www.youtube.com/embed/HZ7yTDYj8To?si=58DsK_ytnW7bhJYe"
                            },
                          ]}
                        />
                      </Suspense>
                    </Col>
                    <Col className="d-flex justify-content-end align-items-center">
                      <Suspense
                        fallback={<div>Loading Recommendations...</div>}
                      >
                        <RecommendationsButton onResultsPage={true} assessmentId={assessmentId} />
                      </Suspense>
                      <Suspense fallback={<div>Loading score table...</div>}>
                        <ScoreInterpretationTable />
                      </Suspense>
                    </Col>
                  </Row>
                  <Accordion
                    activeKey={activeAccordionKey}
                    onSelect={handleSecondaryAccordionSelect}
                    className="accordion-container"
                  >
                    {formattedClassData.map((classData) => {
                      const classResults = classesResults[classData.id] || {};
                      const transformedAssessmentResults = Object.values(
                        classResults.students || {}
                      )
                        .map((student) => ({
                          student: student,
                          tasks: (student.results || []).flatMap((result) =>
                            result.groupedTasks.map((group) => ({
                              name: group.name,
                              standardScore:
                                group.aggregated?.standardScore ?? 0,
                              zScore: group.aggregated?.zScore ?? 0,
                              sTenScore: group.aggregated?.sTenScore ?? 0,
                              duration: group.aggregated?.duration ?? 0,
                            }))
                          ),
                        }))
                        .filter((student) => student.tasks.length > 0);

                      return (
                        <Accordion.Item
                          eventKey={String(classData.id)}
                          key={classData.id}
                        >
                          <Accordion.Header
                            onClick={() =>
                              !classesResults[classData.id] &&
                              handleClick(classData.id)
                            }
                          >
                            {classData.name}
                            {loadingClassId === classData.id && <Fallback />}
                          </Accordion.Header>
                          <Accordion.Body
                            ref={(el) => {
                              if (activeAccordionKey === String(classData.id) && el) {
                                recommendationsBodyRef.current = el;
                              }
                            }}
                          >
                            {classResults ? (
                              <>
                                {transformedAssessmentResults.length > 0 ? (
                                  <Suspense fallback={<Fallback />}>
                                    <TaskScoresComparisonChart
                                      assessmentResultsByStudent={
                                        transformedAssessmentResults
                                      }
                                      fromSencoResults={true}
                                      className={classData.name}
                                      academicYear={academicYearLabel}
                                      assessmentName={assessment.name}
                                    />
                                  </Suspense>
                                ) : (
                                  <div>
                                    No class data available to display chart
                                  </div>
                                )}

                                <Suspense fallback={<Fallback />}>
                                  <TaskScoresComparisonChartSchool
                                    assessmentResultsByStudent={[]}
                                    fromSencoResults={true}
                                    className={classData.name}
                                    averageStandardScoresPerTask={
                                      averageStandardScoresPerTask
                                    }
                                    academicYear={academicYearLabel}
                                    assessmentName={assessment.name}
                                  />
                                </Suspense>
                              </>
                            ) : (
                              <div>
                                Loading data or select this class to load
                                data...
                              </div>
                            )}
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                </Tab>

                <Tab
                  eventKey="settings"
                  title="Planning SET"
                  onEnter={() => {
                    classList.forEach((classItem) => {
                      if (!classesResults[classItem.classId]) {
                        handleClick(classItem.classId);
                      }
                    });
                  }}
                >
                  <Row className="page-description-bar align-items-center">
                    <Col className="col-auto">
                      <Suspense
                        fallback={<div>Loading Assessment Dropdown...</div>}
                      >
                        <SencoAssessmentDropdown
                          classAssessments={classAssessments}
                          classIds={classIds}
                          teachers={teachers}
                          assessmentName={assessment.name}
                          assessment={assessment}
                          teacherid={teacherid}
                          schoolid={schoolid}
                          gradeLevel={gradeLevel}
                          allClassesAssessments={allClassesAssessments}
                        />
                      </Suspense>
                    </Col>
                    <Col className="col-1">
                    </Col>
                    <Col className="d-flex justify-content-center align-items-center">
                      <Suspense fallback={null}>
                        <VideoModal
                          buttonText="Understanding Planning SET"
                          modalTitle="Understanding Planning SET"
                          buttonBackgroundColor="#9D00FF"
                          buttonTextColor="#ffffff"
                          buttonFontSize="75%"
                          buttonWidth="100%"
                          videos={[
                            {
                              title: "Understanding Planning SET",
                              iframe: "https://www.youtube.com/embed/AGB98N14PpM?si=fIeZXnlZO7VacAl1"
                            },
                          ]}
                        />
                      </Suspense>
                    </Col>
                    <Col className="d-flex justify-content-end align-items-center">
                      <Suspense
                        fallback={<div>Loading Recommendations...</div>}
                      >
                        <RecommendationsButton onResultsPage={true} assessmentId={assessmentId} />
                      </Suspense>
                      <Suspense fallback={<div>Loading score table...</div>}>
                        <ScoreInterpretationTable />
                      </Suspense>
                    </Col>
                  </Row>

                  <Accordion
                    activeKey={allStudentsAccordionKey}
                    onSelect={(k) => setAllStudentsAccordionKey(k)}
                  >
                    <Accordion.Item eventKey="all-students">
                      <Accordion.Header>
                        All Students Performance
                      </Accordion.Header>
                      <Accordion.Body
                        ref={(el) => { if (el) allStudentsBodyRef.current = el; }}
                      >
                        <Suspense fallback={<div>Loading chart...</div>}>
                          <AllStudentsPerformancePieChart
                            allStudentsResults={
                              Object.values(classesResults).reduce((allStudents, classResult) => {
                                const yearRange = academicYear;
                                const studentsFromClass = Object.entries(classResult.students || {})
                                  .map(([studentId, student]) => ({
                                    student: { ...student, id: studentId },
                                    tasks: (student.results || [])
                                      .filter((result) => {
                                        if (!yearRange || !result.date) return true;
                                        const d = new Date(result.date);
                                        return d >= new Date(yearRange.start) && d < new Date(yearRange.end);
                                      })
                                      .flatMap((result) =>
                                        result.groupedTasks.map((group) => ({
                                          name: group.name,
                                          score: group.aggregated?.score ?? 0,
                                          standardScore: group.aggregated?.standardScore ?? 0,
                                          zScore: group.aggregated?.zScore ?? 0,
                                          sTenScore: group.aggregated?.sTenScore ?? 0,
                                          duration: group.aggregated?.duration ?? 0,
                                          order: group.order || 0,
                                        }))
                                      ),
                                  }))
                                  .filter(student => student.tasks.length > 0);

                                return [...allStudents, ...studentsFromClass];
                              }, [])
                            }
                            thresholds={planningSetThresholds}
                            classDetails={formattedClassData}
                            classAssessments={classAssessments}
                            gradeLevel={gradeLevel}
                            teachers={teachers}
                            assessment={assessment}
                            toggleScoreType={toggleScoreType}
                            schoolid={schoolid}
                            teacherid={teacherid}
                            fromSencoResults={fromSencoResults}
                          />
                        </Suspense>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>

                  <hr />

                  <Accordion
                    activeKey={activeAccordionKey}
                    onSelect={handleSecondaryAccordionSelect}
                    className="accordion-container"
                  >
                    {formattedClassData.map((classData) => {
                      classRefs.current[classData.id] =
                        classRefs.current[classData.id] || React.createRef();
                      const classResults = classesResults[classData.id] || {};
                      const yearRange = academicYear;
                      const transformedAssessmentResults = Object.entries(
                        classResults.students || {}
                      )
                        .map(([studentId, student]) => ({
                          student: { ...student, id: studentId },
                          tasks: (student.results || [])
                            .filter((result) => {
                              if (!yearRange || !result.date) return true;
                              const d = new Date(result.date);
                              return d >= new Date(yearRange.start) && d < new Date(yearRange.end);
                            })
                            .flatMap((result) =>
                              result.groupedTasks.map((group) => ({
                                name: group.name,
                                score: group.aggregated?.score ?? 0,
                                standardScore:
                                  group.aggregated?.standardScore ?? 0,
                                zScore: group.aggregated?.zScore ?? 0,
                                sTenScore: group.aggregated?.sTenScore ?? 0,
                                duration: group.aggregated?.duration ?? 0,
                              }))
                            ),
                        }))
                        .filter((student) => student.tasks.length > 0);
                      return (
                        <Accordion.Item
                          eventKey={String(classData.id)}
                          key={classData.id}
                        >
                          <Accordion.Header
                            onClick={() =>
                              !classesResults[classData.id] &&
                              handleClick(classData.id)
                            }
                          >
                            {classData.name}
                            {loadingClassId === classData.id && <Fallback />}
                          </Accordion.Header>
                          <Accordion.Body
                            ref={(el) => {
                              if (activeAccordionKey === String(classData.id) && el) {
                                planningSetBodyRef.current = el;
                              }
                            }}
                          >
                            <Row>
                              <Col className="blue-button-container">
                                <button
                                  className="blue-action-button"
                                  style={{ marginBottom: "16px" }}
                                  onClick={() => {
                                    setShowPrintView(!showPrintView);
                                    setShowStudentPerformancePrintView(!showPrintView);
                                  }}
                                >
                                  {showPrintView
                                    ? "Hide Alphabetical Print View"
                                    : "View Alphabetical Print View"}
                                </button>
                              </Col>
                            </Row>
                            {hasResults(classResults) ? (
                              <>
                                {showStudentPerformancePrintView && (
                                  <>
                                    <ReactToPrint
                                      trigger={() => (
                                        <button className="blue-button">
                                          Print Alphabetical View
                                        </button>
                                      )}
                                      content={() => classRefs.current[classData.id].current}
                                    />
                                    <div ref={classRefs.current[classData.id]}>
                                      <Suspense fallback={<div>Loading print...</div>}>
                                        <StudentPerformancePrintViewAlphabetical
                                          assessmentResultsByStudent={transformedAssessmentResults}
                                          thresholds={planningSetThresholds}
                                        />
                                      </Suspense>
                                    </div>
                                  </>
                                )}
                                {!showPrintView && (
                                  <Suspense fallback={<div>Loading chart...</div>}>
                                    <StudentPerformancePieChart
                                      assessmentResultsByStudent={transformedAssessmentResults}
                                      thresholds={planningSetThresholds}
                                      classDetails={classData}
                                      classAssessments={classAssessments}
                                      gradeLevel={gradeLevel}
                                      teachers={teachers}
                                      assessment={assessment}
                                      toggleScoreType={toggleScoreType}
                                      schoolid={schoolid}
                                      teacherid={teacherid}
                                      fromSencoResults={fromSencoResults}
                                    />
                                  </Suspense>
                                )}
                              </>
                            ) : (
                              <div className="no-results-message" style={{
                                textAlign: 'center',
                                padding: '2rem',
                                color: '#6c757d',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                borderRadius: '0.5rem',
                                margin: '1rem 0'
                              }}>
                                <h5>No Results Available</h5>
                                <p>
                                  There are currently no results available for this screener and class.
                                  Please check back later when this class has finished screening.
                                </p>
                              </div>
                            )}
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                </Tab>

                <Tab eventKey="results3" title="Individual Child">
                  <Row className="page-description-bar align-items-center">
                    <Col className="col-auto">
                      <Suspense
                        fallback={<div>Loading Assessment Dropdown...</div>}
                      >
                        <SencoAssessmentDropdown
                          classAssessments={classAssessments}
                          classIds={classIds}
                          teachers={teachers}
                          assessmentName={assessment.name}
                          assessment={assessment}
                          teacherid={teacherid}
                          schoolid={schoolid}
                          gradeLevel={gradeLevel}
                          allClassesAssessments={allClassesAssessments}
                        />
                      </Suspense>
                    </Col>
                    <Col className="col-1">
                    </Col>
                    <Col className="d-flex justify-content-center align-items-center">
                      <Suspense fallback={null}>
                        <VideoModal
                          buttonText="Understanding Individual Child"
                          modalTitle="Understanding Individual Child"
                          buttonBackgroundColor="#9D00FF"
                          buttonTextColor="#ffffff"
                          buttonFontSize="75%"
                          buttonWidth="100%"
                          videos={[
                            {
                              title: "Understanding Individual Child",
                              iframe: "https://www.youtube.com/embed/IaYfDWzneCc?si=ot5ASeXPKRtVneL_"
                            },
                          ]}
                        />
                      </Suspense>
                    </Col>
                    <Col className="d-flex justify-content-end align-items-center">
                      <Suspense
                        fallback={<div>Loading Recommendations...</div>}
                      >
                        <RecommendationsButton onResultsPage={true} assessmentId={assessmentId} />
                      </Suspense>
                      <Suspense fallback={<div>Loading score table...</div>}>
                        <StandardScoreInterpretationTable />
                      </Suspense>
                    </Col>
                  </Row>
                  <span title={!selectedChild ? "Please search for a student first" : !progressGraphLoaded ? "Please wait for the graph to finish loading" : ""}>
                  <button
                    className="blue-button"
                    onClick={() =>
                      setShowChildPrintOptions(!showChildPrintOptions)
                    }
                    disabled={!selectedChild || !progressGraphLoaded}
                    style={(!selectedChild || !progressGraphLoaded) ? { opacity: 0.4 } : {}}
                  >
                    {showChildPrintOptions
                      ? "Hide Print Options"
                      : "View Print Options"}
                  </button>
                  </span>
                  {showChildPrintOptions && (
                    <div className="print-options">
                      <ReactToPrint
                        trigger={() => (
                          <button className="blue-button">
                            Print Individual Child
                          </button>
                        )}
                        content={() => individualChildRef.current}
                      />
                      <ReactToPrint
                        trigger={() => (
                          <button className="blue-button">
                            Print Standard Scores Graph
                          </button>
                        )}
                        content={() => standardScoreGraphRef.current}
                      />
                      <ReactToPrint
                        trigger={() => (
                          <button className="blue-button">
                            Print Progress Between Testing Points
                          </button>
                        )}
                        content={() => candyBearRef.current}
                      />
                    </div>
                  )}
                  <Suspense fallback={<Fallback />}>
                    <AllStudentsIndividualSearch
                      allStudentsResults={allStudentsResults}
                      selectedChild={selectedChild}
                      setSelectedChild={setSelectedChild}
                    />
                  </Suspense>
                  <hr></hr>
                  <div ref={(el) => { individualChildRef.current = el; individualChildBodyRef.current = el; }}>
                    <div ref={standardScoreGraphRef}>
                      <Suspense fallback={<Fallback />}>
                        <StandardScoresGraph selectedChild={selectedChild} />
                      </Suspense>
                      <Suspense fallback={<Fallback />}>
                        <ScoreStatements selectedChild={selectedChild} />
                      </Suspense>
                    </div>
                    <hr></hr>
                    <div ref={candyBearRef}>
                      <Suspense fallback={<Fallback />}>
                        <CandyBearAnalysisGraph
                          selectedChild={selectedChild}
                          setSelectedChildID={setSelectedChildID}
                          individualStudentResult={individualStudentResult}
                        />
                      </Suspense>
                    </div>
                  </div>
                </Tab>

                <Tab
                  eventKey="results4"
                  title="Class Average"
                  className="mb-4"
                  onSelect={(tab) => setActiveTab(tab)}
                  onEnter={() => {
                    classList.forEach((classItem) => {
                      if (!classesResults[classItem.classId]) {
                        handleClick(classItem.classId);
                      }
                    });
                  }}
                >
                  <Row className="page-description-bar align-items-center">
                    <Col className="col-auto">
                      <Suspense
                        fallback={<div>Loading Assessment Dropdown...</div>}
                      >
                        <SencoAssessmentDropdown
                          classAssessments={classAssessments}
                          classIds={classIds}
                          teachers={teachers}
                          assessmentName={assessment.name}
                          assessment={assessment}
                          teacherid={teacherid}
                          schoolid={schoolid}
                          gradeLevel={gradeLevel}
                          allClassesAssessments={allClassesAssessments}
                        />
                      </Suspense>
                    </Col>
                    <Col className="col-1">
                    </Col>
                    <Col className="d-flex justify-content-center align-items-center">
                      <Suspense fallback={null}>
                        <VideoModal
                          buttonText="Understanding Class Average"
                          modalTitle="Understanding Class Average"
                          buttonBackgroundColor="#9D00FF"
                          buttonTextColor="#ffffff"
                          buttonFontSize="75%"
                          buttonWidth="100%"
                          videos={[
                            {
                              title: "Understanding Class Average",
                              iframe: "https://www.youtube.com/embed/2GxOz6h-Wm0?si=erHFJ5PFcSApeKCw"
                            },
                          ]}
                        />
                      </Suspense>
                    </Col>
                    <Col className="d-flex justify-content-end align-items-center">
                      <Suspense
                        fallback={<div>Loading Recommendations...</div>}
                      >
                        <RecommendationsButton onResultsPage={true} assessmentId={assessmentId} />
                      </Suspense>
                      <Suspense fallback={<div>Loading score table...</div>}>
                        <ScoreInterpretationTable />
                      </Suspense>
                    </Col>
                  </Row>
                  <button
                    className="blue-button"
                    onClick={() =>
                      setShowChildPrintOptions(!showChildPrintOptions)
                    }
                  >
                    {showChildPrintOptions
                      ? "Hide Print Options"
                      : "View Print Options"}
                  </button>
                  {showChildPrintOptions && (
                    <div className="print-options">
                      <ReactToPrint
                        trigger={() => (
                          <button className="blue-button">
                            Print Class Averages
                          </button>
                        )}
                        content={() => individualChildRef.current}
                      />
                    </div>
                  )}
                  <br></br>
                  <Tabs
                    defaultActiveKey="classAverageComparison"
                    id="class-average-sub-tabs "
                    style={{ marginBottom: '2rem' }}
                    onSelect={(k) => setActiveClassAverageSubTab(k || "classAverageComparison")}
                  >
                    <Tab
                      eventKey="classAverageComparison"
                      title="Class Average Comparison (Current Screener)"
                      onSelect={(tab) => setActiveTab(tab)}
                      onEnter={() => {
                        classList.forEach((classItem) => {
                          if (!classesResults[classItem.classId]) {
                            handleClick(classItem.classId);
                          }
                        });
                      }}
                    >
                      <div ref={(el) => { individualChildRef.current = el; classAvgComparisonBodyRef.current = el; }}>
                        <div ref={candyBearRef}>
                          <Suspense fallback={<Fallback />}>
                            <CandyBearAnalysisGraphClassComparison
                              assessmentResultsByStudent={
                                assessmentResultsByStudent
                              }
                              formattedClassData={formattedClassData}
                            />
                          </Suspense>
                        </div>
                      </div>
                    </Tab>
                    <Tab
                      eventKey="classAverage"
                      title="Class Average"
                      onSelect={(tab) => setActiveTab(tab)}
                    >
                      <div ref={(el) => { individualChildRef.current = el; classAvgClassBodyRef.current = el; }}>
                        <Accordion
                          activeKey={activeAccordionKey}
                          onSelect={handleSecondaryAccordionSelect}
                          defaultActiveKey="0"
                        >
                          {formattedClassData?.map((classData) => {
                            if (
                              classData?.name?.toUpperCase()?.includes("SET")
                            ) {
                              return null;
                            }

                            const classResults =
                              classesResults[classData.id] || {};

                            const transformedAssessmentResults = Object.values(
                              classResults.students || {}
                            )
                              .map((student) => ({
                                student,
                                tasks: (student.results || []).flatMap(
                                  (result) =>
                                    result.groupedTasks.map((group) => ({
                                      name: group.name,
                                      score: group.aggregated?.score ?? 0,
                                      standardScore:
                                        group.aggregated?.standardScore ?? 0,
                                      zScore: group.aggregated?.zScore ?? 0,
                                      sTenScore:
                                        group.aggregated?.sTenScore ?? 0,
                                      duration: group.aggregated?.duration ?? 0,
                                    }))
                                ),
                              }))
                              .filter((student) => student.tasks.length > 0);

                            return (
                              <Accordion.Item
                                key={classData.id}
                                eventKey={String(classData.id)}
                              >
                                <Accordion.Header
                                  onClick={() =>
                                    !classesResults[classData.id] &&
                                    handleClick(classData.id)
                                  }
                                >
                                  {classData.name}
                                  {loadingClassId === classData.id && (
                                    <Fallback />
                                  )}
                                </Accordion.Header>
                                <Accordion.Body>
                                  {classResults ? (
                                    <Suspense fallback={<Fallback />}>
                                      <CandyBearAnalysisGraphClassAverage
                                        selectedChild={selectedChild}
                                        setSelectedChildID={setSelectedChildID}
                                        individualStudentResult={
                                          individualStudentResult
                                        }
                                        assessmentResultsByStudent={
                                          transformedAssessmentResults
                                        }
                                        classId={classData.id}
                                        className={classData.name}
                                        classAssessments={classAssessments}
                                        gradeLevel={gradeLevel}
                                        schoolid={schoolid}
                                        teacherid={teacherid}
                                      />
                                    </Suspense>
                                  ) : (
                                    <div>
                                      Loading data or select this class to load
                                      data...
                                    </div>
                                  )}
                                </Accordion.Body>
                              </Accordion.Item>
                            );
                          })}
                        </Accordion>
                      </div>
                    </Tab>
                  </Tabs>
                </Tab>

                <Tab
                  eventKey="progress"
                  title="Progress"
                  className="mb-4"
                >
                  <Row className="page-description-bar align-items-center">
                    <Col className="d-flex justify-content-end align-items-center">
                      <Suspense fallback={<div>Loading Recommendations...</div>}>
                        <RecommendationsButton onResultsPage={true} assessmentId={assessmentId} />
                      </Suspense>
                      <Suspense fallback={<div>Loading score table...</div>}>
                        <ScoreInterpretationTable />
                      </Suspense>
                    </Col>
                  </Row>

                  <Tabs
                    defaultActiveKey="screeningProgressComparison"
                    id="class-progress-sub-tabs"
                    style={{ marginBottom: '2rem' }}
                    onSelect={(k) => setActiveProgressSubTab(k || "screeningProgressComparison")}
                  >
                    <Tab
                      eventKey="screeningProgressComparison"
                      title="Screening Point"
                    >
                      <div ref={(el) => { individualChildRef.current = el; screeningPointBodyRef.current = el; }}>
                        <h4 className="mt-5 mb-3">Class Screening Progress</h4>
                        <Accordion
                          onSelect={(key) => {
                            handleSecondaryAccordionSelect(key);
                            if (key && !openedAccordions.has(key)) {
                              setOpenedAccordions(prev => new Set([...prev, key]));
                            }
                          }}
                        >
                          {formattedClassData.map((classData) => {
                            return (
                              <Accordion.Item
                                key={classData.id}
                                eventKey={String(classData.id)}
                              >
                                <Accordion.Header>
                                  {classData.name}
                                  {loadingClassId === classData.id && <Fallback />}
                                </Accordion.Header>
                                <Accordion.Body>
                                  {openedAccordions.has(String(classData.id)) ? (
                                    <Suspense fallback={
                                      <div className="text-center p-3">
                                        <BigSpinner />
                                        <p>Loading screening data...</p>
                                      </div>
                                    }>
                                      <ScreenerPieCharts
                                        schoolId={schoolid}
                                        classId={classData.id}
                                        className={classData.name}
                                        timeframe={academicYearTimeframe}
                                        gradeLevel={gradeLevelKey}
                                        assessmentIds={selectedAssessmentIds}
                                      />
                                    </Suspense>
                                  ) : (
                                    <div className="text-center p-3">
                                      <p>Click to expand and load screening progress data...</p>
                                    </div>
                                  )}
                                </Accordion.Body>
                              </Accordion.Item>
                            );
                          })}
                        </Accordion>
                      </div>
                    </Tab>

                    <Tab
                      eventKey="classroomProgress"
                      title="Classroom"
                    >
                      <div ref={classroomProgressBodyRef}>
                        <Suspense fallback={
                          <div className="text-center p-4">
                            <BigSpinner />
                            <p>Loading classroom progress component...</p>
                          </div>
                        }>
                          <ClassroomProgressTabContent
                            selectedAssessmentIds={selectedAssessmentIds}
                            schoolid={schoolid}
                            timeframe={academicYearTimeframe}
                            individualChildRef={individualChildRef}
                          />
                        </Suspense>
                      </div>
                    </Tab>
                  </Tabs>
                </Tab>

                <Tab eventKey="Not Screened" title="Screening Status">
                  <>
                    {isLoading ? (
                      <div className="spinner-container">
                        <div className="big-ball-spinner"></div>
                      </div>
                    ) : (
                      <>
                        <Row className="page-description-bar align-items-center">
                          <Col className="col-auto">
                            <Suspense
                              fallback={
                                <div>Loading Assessment Dropdown...</div>
                              }
                            >
                              <SencoAssessmentDropdown
                                classAssessments={classAssessments}
                                classIds={classIds}
                                teachers={teachers}
                                assessmentName={assessment.name}
                                assessment={assessment}
                                teacherid={teacherid}
                                schoolid={schoolid}
                                gradeLevel={gradeLevel}
                                allClassesAssessments={allClassesAssessments}
                              />
                            </Suspense>
                          </Col>
                          <Col className="col-1">
                          </Col>
                          <Col className="d-flex justify-content-center align-items-center">
                            <Suspense fallback={null}>
                              <VideoModal
                                buttonText="Understanding Screening Status"
                                modalTitle="Understanding Screening Status"
                                buttonBackgroundColor="#9D00FF"
                                buttonTextColor="#ffffff"
                                buttonFontSize="75%"
                                buttonWidth="100%"
                                videos={[
                                  {
                                    title: "Understanding Screening Status",
                                    iframe: "https://www.youtube.com/embed/AsRKwzky3Mg?si=H79u3gFBR5JaqDoC"
                                  },
                                ]}
                              />
                            </Suspense>
                          </Col>
                          <Col className="d-flex justify-content-end align-items-center">
                            <Suspense
                              fallback={<div>Loading Recommendations...</div>}
                            >
                              <RecommendationsButton onResultsPage={true} assessmentId={assessmentId} />
                            </Suspense>
                            <Suspense
                              fallback={<div>Loading score table...</div>}
                            >
                              <ScoreInterpretationTable />
                            </Suspense>
                          </Col>
                        </Row>
                        <div ref={screeningStatusRef}>
                        <Accordion
                          activeKey={activeNotScreenedKey}
                          onSelect={(k) => setActiveNotScreenedKey(k)}
                          className="accordion-container"
                        >
                          {classList.map((classItem, index) => {
                            const { classId, name } = classItem;

                            return (
                              <Accordion.Item
                                eventKey={String(index)}
                                key={classId}
                                className="accordion-item-results"
                              >
                                <Accordion.Header
                                  onClick={(e) => {
                                    handleAccordionToggle(
                                      String(index),
                                      activeAccordionRef,
                                      name,
                                      classId
                                    );
                                    if (
                                      !e.target.closest(
                                        ".accordion-actions-icon"
                                      )
                                    ) {
                                      handleClick(classId);
                                    }
                                  }}
                                >
                                  <div className="d-flex justify-content-between w-100 align-items-center">
                                    <span>{name}</span>
                                  </div>
                                  {loadingClassId === classId && <Fallback />}
                                </Accordion.Header>
                                <Accordion.Body
                                  ref={(el) => {
                                    if (el && activeNotScreenedKey === String(index)) {
                                      notScreenedBodyRef.current = el;
                                    }
                                  }}
                                >
                                  {classesResults[classId] ? (
                                    <Suspense
                                      fallback={
                                        <div>Loading not screened data...</div>
                                      }
                                    >
                                      <NotScreenedTab
                                        classesResults={{
                                          [classId]: classesResults[classId],
                                        }}
                                        schoolid={schoolid}
                                        teacherid={teacherid}
                                        classId={classId}
                                        assessment={assessment}
                                        classAssessments={classAssessments}
                                        gradeLevel={gradeLevel}
                                        fromSencoResults={fromSencoResults}
                                        students={students}
                                      />
                                    </Suspense>
                                  ) : (
                                    <p>Loading data for this class...</p>
                                  )}
                                </Accordion.Body>
                              </Accordion.Item>
                            );
                          })}
                        </Accordion>
                        </div>
                      </>
                    )}
                  </>
                </Tab>
              </Tabs>
            </Suspense>
          </Container>
        </div>
      </div>
      {openCard === "printCard" && (
        <ReportActionCard
          cardPosition={printCardPosition}
          handleCloseCard={() => toggleCard("printCard")}
          activeAccordionRef={activeAccordionRef}
          activeAccordionStudents={activeAccordionStudents}
          toAllStudentResults={toAllStudentResults}
          toReportCards={toReportCards}
          activeAccordionClassId={activeAccordionClassId}
          setViewParent={setViewParent}
          setViewTeacher={setViewTeacher}
          assessmentName={assessment?.name}
        />
      )}
      {openCard === "shareCard" && (
        <ShareActionCard
          cardPosition={shareCardPosition}
          handleCloseCard={() => toggleCard("shareCard")}
          activeAccordionRef={activeAccordionRef}
          activeAccordionStudents={activeAccordionStudents}
          shareResults={(zipBlob) => {
            console.log("Sharing ZIP Blob:", zipBlob);
          }}
          assessmentName={assessment?.name || "Assessment"}
          className={activeClassName || "Class"}
          rollNumber={schoolid || "School"}
          setViewParent={setViewParent}
          setViewTeacher={setViewTeacher}
          schoolid={schoolid}
          teacherId={teacherid}
          assessmentIds={[assessment?.assessmentid || assessment?.id].filter(Boolean)}
        />
      )}
      {accordionSelectedClass && openCard === "actionCard" && (
        <ActionCard
          selectedStudent={accordionSelectedClass}
          taskList={classTaskList.map((task) => ({
            name: task.name,
            answerid: task.name,
          }))}
          selectedTasks={selectedClassTasks}
          selectAll={selectAllClassTasks}
          showTaskList={showClassTaskList}
          cardPosition={accordionCardPosition}
          actionCardRef={accordionActionCardRef}
          handleCloseCard={() => toggleCard("actionCard")}
          setShowTaskList={setShowClassTaskList}
          handleTaskSelection={handleClassTaskSelection}
          handleSelectAll={handleSelectAllClassTasks}
          handleRetake={handleClassRetake}
          toAllStudentResults={toAllStudentResults}
          setViewParent={setViewParent}
          setViewTeacher={setViewTeacher}
          assessmentName={assessment?.name}
        />
      )}
      {classTaskToDelete && openCard === "deleteModal" && (
        <DeleteAnswerModal
          show={showClassDeleteModal}
          handleClose={() => {
            setShowClassDeleteModal(false);
            toggleCard("deleteModal");
          }}
          taskName={classTaskToDelete.taskName}
          studentName={accordionSelectedClass?.name || "Selected Class"}
          assessmentName={assessment?.name || "Assessment"}
          handleDelete={handleClassDeleteSuccess}
          answerIds={classTaskToDelete.answerIds}
          teacherid={teacherid}
          isRetakeFullAssessment={classTaskToDelete.isFullAssessment}
          isClassLevel={true}
        />
      )}
      {openCard === "printActionCard" && (
        <PrintActionCardSenco
          cardPosition={printActionCardPosition}
          handleCloseCard={() => toggleCard("printActionCard")}
          activeAccordionRef={activeAccordionRef}
          activeAccordionKey={activeAccordionKey}
          performanceBodyRef={performanceBodyRef}
          recommendationsBodyRef={recommendationsBodyRef}
          planningSetBodyRef={planningSetBodyRef}
          allStudentsBodyRef={allStudentsBodyRef}
          allStudentsAccordionKey={allStudentsAccordionKey}
          individualChildBodyRef={individualChildBodyRef}
          classAvgComparisonBodyRef={classAvgComparisonBodyRef}
          classAvgClassBodyRef={classAvgClassBodyRef}
          activeClassAverageSubTab={activeClassAverageSubTab}
          screeningPointBodyRef={screeningPointBodyRef}
          classroomProgressBodyRef={classroomProgressBodyRef}
          activeProgressSubTab={activeProgressSubTab}
          screeningStatusBodyRef={notScreenedBodyRef}
          academicYear={academicYearLabel}
          className={activeClassName}
          assessmentName={assessment.name}
          activeTab={activeTab}
        />
      )}

      <Suspense fallback={<div>Loading...</div>}>
        <StandardScoreRegionLockModal
          show={showRegionLockModal}
          onHide={() => setShowRegionLockModal(false)}
          featureName={attemptedTab}
        />
      </Suspense>

      <Suspense fallback={null}>
        <StandardScoreUnavailableModal
          show={showUnavailableModal}
          onHide={() => setShowUnavailableModal(false)}
        />
      </Suspense>
    </>
  );
}

export default SencoStudentsResultsOverview;
